import React, { useState, useEffect, useContext } from "react";
import {
  Container,
} from "./styles";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { getAllAccessLevels } from "../../api/autenticoAPI";
import { resetUserPassword, updateAccessLevel, getTeachersAllocations, deleteUser } from "../../api/autenticoAPI";
import { CustomAlert } from "../CustomAlert/CustomAlert";
import { TeacherAllocationsForm } from "./TeacherAllocationsForm";
import { AuthContext } from "../../contexts/Auth/AuthContext";

const levelOptions = [
  { value: "admin", label: "ADMINISTRADOR" },
  { value: "assistant", label: "AUXILIAR" },
  { value: "caixa", label: "CAIXA" },
  { value: "coordinator", label: "COORDENADOR" },
  { value: "director", label: "DIRETOR" },
  { value: "teacher", label: "PROFESSOR" },
];

// Níveis de acesso que coordenadores podem editar
const coordinatorEditableLevels = [
  { value: "assistant", label: "AUXILIAR" },
  { value: "caixa", label: "CAIXA" },
  { value: "teacher", label: "PROFESSOR" },
];

const levelLabels = {
  admin: "ADMINISTRADOR",
  assistant: "AUXILIAR",
  caixa: "CAIXA",
  coordinator: "COORDENADOR",
  director: "DIRETOR",
  teacher: "PROFESSOR",
};

export function UserList() {
  const auth = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [editName, setEditName] = useState("");
  const [editLogin, setEditLogin] = useState("");
  const [editLevel, setEditLevel] = useState("");
  const [teacherAllocations, setTeacherAllocations] = useState([]);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [usersAllocations, setUsersAllocations] = useState({}); // Mapa de userId -> alocações
  const [infoType, setInfoType] = useState("success");
  const [status, setStatus] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");

  useEffect(() => {
    loadUsers();
  }, []);

  // Carregar alocações de todos os professores após carregar usuários
  useEffect(() => {
    const loadAllTeachersAllocations = async () => {
      const teachers = users.filter(u => u.level === "teacher");
      if (teachers.length === 0) {
        setUsersAllocations({});
        return;
      }

      const allocationsMap = {};

      for (const teacher of teachers) {
        try {
          const allocations = await getTeachersAllocations(teacher.id, null, null);
          // Desagrupar se necessário
          let normalized = [];
          if (Array.isArray(allocations)) {
            allocations.forEach(a => {
              if (a.Disciplinas && Array.isArray(a.Disciplinas)) {
                a.Disciplinas.forEach(disciplina => {
                  normalized.push({
                    TurmaID: a.TurmaID || a.turmaID,
                    turmaName: a.turmaName || a.turma_name,
                    DisciplinaID: disciplina.DisciplinaID || disciplina.disciplinaID,
                    disciplinaName: disciplina.disciplinaName || disciplina.disciplina_name,
                  });
                });
              } else {
                normalized.push({
                  TurmaID: a.TurmaID || a.turmaID,
                  turmaName: a.turmaName || a.turma_name,
                  DisciplinaID: a.DisciplinaID || a.disciplinaID,
                  disciplinaName: a.disciplinaName || a.disciplina_name,
                });
              }
            });
          }
          allocationsMap[teacher.id] = normalized;
        } catch (error) {
          console.error(`Erro ao carregar alocações do professor ${teacher.id}:`, error);
          allocationsMap[teacher.id] = [];
        }
      }

      setUsersAllocations(allocationsMap);
    };

    if (users.length > 0) {
      loadAllTeachersAllocations();
    }
  }, [users]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const accessLevels = await getAllAccessLevels();
      // Ordenar por nome alfabeticamente
      const sortedUsers = (accessLevels || []).sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB, 'pt-BR');
      });
      setUsers(sortedUsers);
      setFilteredUsers(sortedUsers);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
      setInfoType("error");
      setInfoMessage("Erro ao carregar lista de usuários");
      setStatus(true);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar usuários baseado no termo de pesquisa
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }

    const search = searchTerm.toLowerCase().trim();
    const filtered = users.filter(user => {
      const name = (user.name || '').toLowerCase();
      const login = (user.login || '').toLowerCase();
      return name.includes(search) || login.includes(search);
    });

    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleOpenResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setConfirmPassword("");
    setResetDialogOpen(true);
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setSelectedUser(null);
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleOpenEditDialog = async (user) => {
    setSelectedUser(user);
    setEditName(user.name);
    setEditLogin(user.login || "");
    setEditLevel(user.level);
    setEditDialogOpen(true);

    // Se for professor, carregar alocações existentes
    if (user.level === "teacher" && user.id) {
      await loadTeacherAllocations(user.id);
    } else {
      setTeacherAllocations([]);
    }
  };

  const loadTeacherAllocations = async (teacherId) => {
    try {
      setLoadingAllocations(true);
      const allocations = await getTeachersAllocations(teacherId, null, null);
      // O backend pode retornar alocações agrupadas ou não agrupadas
      // Precisamos desagrupar se estiver agrupado
      let normalized = [];

      if (Array.isArray(allocations)) {
        allocations.forEach(a => {
          // Se tiver Disciplinas (formato agrupado), desagrupar
          if (a.Disciplinas && Array.isArray(a.Disciplinas)) {
            a.Disciplinas.forEach(disciplina => {
              normalized.push({
                TurmaID: a.TurmaID || a.turmaID,
                turmaName: a.turmaName || a.turma_name || a.turmaName,
                DisciplinaID: disciplina.DisciplinaID || disciplina.disciplinaID,
                disciplinaName: disciplina.disciplinaName || disciplina.disciplina_name,
              });
            });
          } else {
            // Formato não agrupado
            normalized.push({
              TurmaID: a.TurmaID || a.turmaID,
              turmaName: a.turmaName || a.turma_name,
              DisciplinaID: a.DisciplinaID || a.disciplinaID,
              disciplinaName: a.disciplinaName || a.disciplina_name,
            });
          }
        });
      }

      setTeacherAllocations(normalized);
    } catch (error) {
      console.error("Erro ao carregar alocações do professor:", error);
      setTeacherAllocations([]);
    } finally {
      setLoadingAllocations(false);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setEditName("");
    setEditLogin("");
    setEditLevel("");
    setTeacherAllocations([]);
  };

  const handleUpdateUser = async () => {
    if (!editName || !editLogin || !editLevel) {
      setInfoType("error");
      setInfoMessage("Nome, login e nível de acesso são obrigatórios");
      setStatus(true);
      return;
    }

    try {
      // Preparar alocações para enviar ao backend
      let allocationsToSend = null;
      if (editLevel === "teacher") {
        allocationsToSend = teacherAllocations.length > 0 ? teacherAllocations : [];
      }

      // Atualizar dados do usuário (incluindo login e alocações)
      const response = await updateAccessLevel(selectedUser.id, editName, editLogin, editLevel, allocationsToSend);
      if (response && response.status) {
        setInfoType("success");
        setInfoMessage(response.message || "Usuário atualizado com sucesso!");
        setStatus(true);
        handleCloseEditDialog();
        loadUsers(); // Recarregar lista (isso também recarrega as alocações)
      } else {
        setInfoType("error");
        setInfoMessage(response.message || "Erro ao atualizar usuário");
        setStatus(true);
      }
    } catch (error) {
      console.error("Erro completo ao atualizar usuário:", error);
      setInfoType("error");
      setInfoMessage(
        error.response?.data?.message ||
        "Erro ao atualizar usuário. Tente novamente."
      );
      setStatus(true);
    }
  };


  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setInfoType("error");
      setInfoMessage("A senha deve ter pelo menos 6 caracteres");
      setStatus(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setInfoType("error");
      setInfoMessage("As senhas não coincidem");
      setStatus(true);
      return;
    }

    try {
      const response = await resetUserPassword(selectedUser.id, newPassword);
      if (response && response.status) {
        setInfoType("success");
        setInfoMessage(response.message || "Senha resetada com sucesso!");
        setStatus(true);
        handleCloseResetDialog();
        loadUsers(); // Recarregar lista
      } else {
        setInfoType("error");
        setInfoMessage(response.message || "Erro ao resetar senha");
        setStatus(true);
      }
    } catch (error) {
      setInfoType("error");
      setInfoMessage(
        error.response?.data?.message ||
        "Erro ao resetar senha. Tente novamente."
      );
      setStatus(true);
    }
  };

  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const response = await deleteUser(selectedUser.id);
      if (response && response.status) {
        setInfoType("success");
        setInfoMessage(response.message || "Usuário excluído com sucesso!");
        setStatus(true);
        handleCloseDeleteDialog();
        loadUsers(); // Recarregar lista
      } else {
        setInfoType("error");
        setInfoMessage(response.message || "Erro ao excluir usuário");
        setStatus(true);
      }
    } catch (error) {
      setInfoType("error");
      setInfoMessage(
        error.response?.data?.message ||
        "Erro ao excluir usuário. Tente novamente."
      );
      setStatus(true);
    }
  };

  return (
    <Container>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Lista de Usuários
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Clique no ícone de edição para alterar o nome e nível de acesso do usuário.
          Clique em "Resetar Senha" para definir uma nova senha. O usuário precisará alterá-la no próximo login.
        </Typography>
        <TextField
          fullWidth
          placeholder="Pesquisar por nome ou login..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Login</TableCell>
              <TableCell>Nível de Acesso</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {searchTerm ? "Nenhum usuário encontrado com o termo pesquisado" : "Nenhum usuário encontrado"}
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => {
                const userAllocations = usersAllocations[user.id] || [];
                const isTeacher = user.level === "teacher";

                // Verificar se coordenador pode editar/resetar senha deste usuário
                // Coordenadores só podem editar/resetar senha de assistant, caixa e teacher
                const canEditUser = auth.user?.level === "admin" ||
                  (auth.user?.level === "coordinator" && (user.level === "assistant" || user.level === "caixa" || user.level === "teacher"));

                return (
                  <TableRow key={user.id}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.login || "-"}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {levelLabels[user.level] || user.level}
                        </Typography>
                        {isTeacher && (
                          <Box sx={{ mt: 0.5 }}>
                            {userAllocations.length > 0 ? (
                              <Tooltip
                                title={
                                  <Box>
                                    {userAllocations.map((alloc, idx) => (
                                      <Typography key={idx} variant="caption" display="block">
                                        {alloc.turmaName} - {alloc.disciplinaName}
                                      </Typography>
                                    ))}
                                  </Box>
                                }
                                arrow
                              >
                                <Chip
                                  label={
                                    userAllocations.length === 1
                                      ? `${userAllocations.length} alocação`
                                      : `${userAllocations.length} alocações`
                                  }
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontSize: "0.7rem", height: "20px", mt: 0.5 }}
                                />
                              </Tooltip>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontStyle: "italic" }}
                              >
                                Nenhuma alocação
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", alignItems: "center" }}>
                        {canEditUser ? (
                          <>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleOpenEditDialog(user)}
                              title="Editar usuário"
                            >
                              <EditIcon />
                            </IconButton>
                            <Button
                              variant="outlined"
                              color="warning"
                              size="small"
                              onClick={() => handleOpenResetDialog(user)}
                            >
                              Resetar Senha
                            </Button>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                            Sem permissão
                          </Typography>
                        )}
                        {auth.user?.level === "admin" && (
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleOpenDeleteDialog(user)}
                            title="Excluir usuário"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editar Usuário</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            fullWidth
            variant="outlined"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Login"
            fullWidth
            variant="outlined"
            value={editLogin}
            onChange={(e) => setEditLogin(e.target.value)}
            helperText="Altere o login do usuário"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Nível de Acesso</InputLabel>
            <Select
              value={editLevel}
              label="Nível de Acesso"
              onChange={(e) => {
                setEditLevel(e.target.value);
                // Se mudar para não-professor, limpar alocações
                if (e.target.value !== "teacher") {
                  setTeacherAllocations([]);
                } else if (selectedUser && selectedUser.level !== "teacher") {
                  // Se mudou para professor, carregar alocações (vazias)
                  setTeacherAllocations([]);
                }
              }}
            >
              {(() => {
                // Se for coordenador, mostrar apenas auxiliar e professor
                if (auth.user?.level === "coordinator") {
                  return coordinatorEditableLevels.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ));
                }
                // Se for admin, mostrar todos os níveis
                return levelOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ));
              })()}
            </Select>
          </FormControl>

          {/* Mostrar formulário de alocações apenas para professores */}
          {editLevel === "teacher" && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Turmas e Disciplinas do Professor
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Revise as alocações atuais e adicione ou remova turmas e disciplinas conforme necessário.
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#fafafa",
                }}
              >
                {loadingAllocations ? (
                  <Typography variant="body2" color="text.secondary">
                    Carregando alocações...
                  </Typography>
                ) : (
                  <>
                    {teacherAllocations.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Alocações atuais ({teacherAllocations.length})
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            maxHeight: 160,
                            overflowY: "auto",
                            pr: 0.5,
                          }}
                        >
                          {teacherAllocations.map((allocation, index) => (
                            <Chip
                              key={`${allocation.TurmaID}-${allocation.DisciplinaID}-${index}`}
                              label={`${allocation.turmaName} - ${allocation.disciplinaName}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem", height: 22 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <TeacherAllocationsForm
                      allocations={teacherAllocations}
                      onChange={setTeacherAllocations}
                    />
                  </>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancelar</Button>
          <Button
            onClick={handleUpdateUser}
            variant="contained"
            color="primary"
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resetDialogOpen}
        onClose={handleCloseResetDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resetar Senha</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Usuário: <strong>{selectedUser?.name}</strong>
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
            Login: <strong>{selectedUser?.login || "-"}</strong>
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Nova Senha"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Mínimo de 6 caracteres"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Confirmar Nova Senha"
            type="password"
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog}>Cancelar</Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="primary"
          >
            Resetar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Excluir Usuário</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Tem certeza que deseja excluir o usuário <strong>{selectedUser?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Login: <strong>{selectedUser?.login || "-"}</strong>
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Esta ação não pode ser desfeita. Todas as informações do usuário serão removidas permanentemente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button
            onClick={handleDeleteUser}
            variant="contained"
            color="error"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <CustomAlert
        infoType={infoType}
        status={status}
        setStatus={setStatus}
        infoMessage={infoMessage}
      />
    </Container>
  );
}

